'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "option"],
  _excluded2 = ["options"];
import I18n from 'I18n';
import PropTypes from 'prop-types';
import { useEffect, useState, useRef, Fragment } from 'react';
import SyntheticEvent from 'UIComponents/core/SyntheticEvent';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { Select } from 'foundations-components/transitional/components/Select';
import { Typeahead } from 'foundations-components/transitional/components/Typeahead';
import { i18nKey } from '../../utils/properties';
import SettingsLink from './internal/SettingsLink';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getFieldOptions = (propertyOptions, currentCategoryOption, unsupportedTokens = {}) => {
  if (propertyOptions.length === 0) {
    return [];
  }
  const selectedCategory = currentCategoryOption.value;
  if (!selectedCategory) {
    return [];
  }
  const options = propertyOptions.find(({
    value
  }) => value === selectedCategory).options;
  const unsupportedTokensForCategory = unsupportedTokens[selectedCategory];
  if (unsupportedTokensForCategory) {
    return options.filter(option => !unsupportedTokensForCategory.includes(option.name));
  }
  return options;
};
const propTypes = {
  currentOption: PropTypes.object.isRequired,
  dropdownClassName: PropTypes.string,
  emailSettingsLink: PropTypes.string.isRequired,
  onPropertySelect: PropTypes.func.isRequired,
  propertyOptions: PropTypes.array.isRequired
};
const displayName = 'TokenBrowser';
const MaybeDisabledItemComponent = props => {
  const {
      children,
      option
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  return /*#__PURE__*/_jsx(Tooltip, {
    placement: "left",
    title: option.disabledTooltip,
    disabled: !option.disabled,
    children: /*#__PURE__*/_jsx("span", Object.assign({}, rest, {
      onClick: evt => {
        // Prevent duplicate onChange behavior
        evt.stopPropagation();
      },
      children: children
    }))
  });
};
const TokenBrowser = props => {
  const {
    currentOption,
    emailSettingsLink,
    onPropertySelect,
    propertyOptions,
    personalizeConfig
  } = props;
  const [currentCategoryOption, setCurrentCategoryOption] = useState({});
  const [propertySearch, setPropertySearch] = useState('');
  const searchInputRef = useRef(null);
  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [currentCategoryOption.value]);
  useEffect(() => {
    const currentCategory = currentOption.category;
    const categoryOption = propertyOptions.find(option => option.value === currentCategory);
    if (categoryOption) {
      setCurrentCategoryOption(categoryOption);
    } else if (propertyOptions.length > 0) {
      setCurrentCategoryOption(propertyOptions[0]);
    }
  }, [currentOption, propertyOptions]);
  const handleCategorySelect = evt => {
    setCurrentCategoryOption(evt.target.value);
  };
  const handleSearchChange = evt => {
    setPropertySearch(evt.target.value);
  };
  const fieldOptions = getFieldOptions(propertyOptions, currentCategoryOption, personalizeConfig.unsupportedTokens);

  // hack because `UITypeahead` doesn't support an `onSelectedOptionChange`
  // prop, so we need to patch `onChange` to return the whole option
  const handleSelectedOptionChange = evt => {
    const {
      target: {
        value
      }
    } = evt;
    const optionForValue = fieldOptions.find(option => option.value === value);
    onPropertySelect(SyntheticEvent(optionForValue));
  };

  // UISelect gets upset if you pass options that include both their
  // own options property AND a value property as that is technically
  // mixing the two types of possible option shapes
  const categoryOptions = propertyOptions.map(option => {
    const rest = _objectWithoutPropertiesLoose(option, _excluded2);
    return rest;
  });
  const categoryName = currentCategoryOption.text || '';
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx(FormControl, {
      marginBlockEnd: "300",
      marginInline: "500",
      help: /*#__PURE__*/_jsx(SettingsLink, {
        href: emailSettingsLink,
        settingsKey: currentOption.category
      }),
      label: I18n.text(i18nKey('tokenLabel')),
      children: /*#__PURE__*/_jsx(Select, {
        "data-test-id": "properties-ui-select",
        onSelectedOptionChange: handleCategorySelect,
        options: categoryOptions,
        placeholder: I18n.text(i18nKey('tokenPlaceholder')),
        value: currentCategoryOption.value
      })
    }), /*#__PURE__*/_jsx(Typeahead, {
      "data-test-id": "token-search-input",
      itemComponent: MaybeDisabledItemComponent,
      enableKeyboardScrollingNav: true,
      onChange: handleSelectedOptionChange,
      options: fieldOptions,
      onSearchChange: handleSearchChange,
      placeholder: I18n.text(i18nKey('propertySearchPlaceholder'), {
        type: categoryName
      }),
      value: propertySearch,
      inputRef: searchInputRef
    })]
  });
};
TokenBrowser.propCategorys = propTypes;
TokenBrowser.displayName = displayName;
export default TokenBrowser;